import { useFormik } from "formik";
import { Eye, EyeOff, Check, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { Input } from "../../../(components)/ui/input";
import {
  StyledButton,
  buttonStyles,
} from "../../../../../Shared/button/button";
import { passwordCriteria } from "../../../constants/validationRules";
import { personalSchema } from "../../../constants/schema";
import {
  extractIndianNationalFromPhoneInput,
  isIndiaCountryCode,
  toE164IndiaFromPhoneInput,
  toE164International,
} from "../../../../../utils/newOnboarding/validations";
import CategoryDropdown from "./CategoryDropdown";
import ToastSuccess from "../../../../(common)/Toast/ToastSuccess";
import ToastWarning from "../../../../(common)/Toast/ToastWarning";
import { TEXTS } from "../../../constants/texts";
import { callApi } from "../../../lib/api";
import { useNavigate } from "react-router-dom";
import { getSubCategoryOptions } from "../../../../../utils/newOnboarding/utilities";
import { testIdProps } from "../../../../../utils/testIdProps";

export interface CategoryType {
  category_pid: string;
  category: string;
}

export interface FormValuesStep3 {
  name: string;
  businessName: string;
  phone: string;
  countryCode: string;
  password: string;
  confirmPassword: string;
  category: CategoryType;
}

const Step3 = ({
  createdUserData,
  email = createdUserData.email,
}: {
  createdUserData: { relayId: string; email: string; token: string };
  email?: string;
}) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState("IN");

  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [subCategories, setSubCategories] = useState<any[]>([]);

  const formik = useFormik<FormValuesStep3>({
    initialValues: {
      name: "",
      businessName: "",
      phone: "",
      countryCode: "IN",
      password: "",
      confirmPassword: "",
      category: { category_pid: "", category: "" },
    },
    validationSchema: personalSchema(countryCode),
    onSubmit: async (values, { setSubmitting }) => {
      const valuesWithEmail = {
        ...values,
        email,
      };
      await handlePersonalDetails(valuesWithEmail);
      setSubmitting(false);
    },
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: false,
  });

  // Update validation schema when countryCode changes
  useEffect(() => {
    // Force revalidation of the entire form when country changes
    if (formik.touched.phone) {
      formik.validateForm();
    }
  }, [countryCode]);

  // Function to mark all fields as touched when submit is attempted
  const handleSubmitAttempt = async () => {
    // Mark all fields as touched
    formik.setTouched({
      name: true,
      businessName: true,
      phone: true,
      password: true,
      confirmPassword: true,
      category: { category_pid: true, category: true },
    });

    // Force validation of all fields
    await formik.validateForm();
  };

  // Enhanced onChange handlers to trigger validation immediately
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    formik.setFieldTouched("name", true, false);
    // Trigger validation immediately
    setTimeout(() => {
      formik.validateField("name");
    }, 0);
  };

  const handleBusinessNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    formik.setFieldTouched("businessName", true, false);
    // Trigger validation immediately
    setTimeout(() => {
      formik.validateField("businessName");
    }, 0);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    // Clear password error if password is valid
    if (e.target.value && e.target.value.length >= 8) {
      formik.setFieldError("password", undefined);
    }

    // If confirm password has a value, validate it and mark as touched
    if (formik.values.confirmPassword) {
      formik.setFieldTouched("confirmPassword", true, false);
      formik.validateField("confirmPassword");
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    formik.handleChange(e);
    // Mark as touched and validate if both passwords have values
    if (e.target.value && formik.values.password) {
      formik.setFieldTouched("confirmPassword", true, false);
      formik.validateField("confirmPassword");
    }
  };

  // Blur handlers for fields that should only validate on blur
  const handlePasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    formik.setFieldTouched("password", true, false);
  };

  const handleConfirmPasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    formik.setFieldTouched("confirmPassword", true, false);
    // Validate on blur if both passwords have values
    if (e.target.value && formik.values.password) {
      formik.validateField("confirmPassword");
    }
  };

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    formik.setFieldTouched("phone", true, false);
  };

  useEffect(() => {
    if (formik.values.phone) {
      formik.validateField("phone");
    }
  }, [countryCode]);

  useEffect(() => {
    if (formik.touched.name && formik.values.name) {
      formik.validateField("name");
    }
  }, [formik.values.name, formik.touched.name]);

  useEffect(() => {
    if (formik.touched.businessName && formik.values.businessName) {
      formik.validateField("businessName");
    }
  }, [formik.values.businessName, formik.touched.businessName]);

  useEffect(() => {
    if (formik.touched.password && formik.values.password) {
      formik.validateField("password");
    }
  }, [formik.values.password, formik.touched.password]);

  useEffect(() => {
    if (formik.touched.confirmPassword && formik.values.confirmPassword) {
      formik.validateField("confirmPassword");
    }
  }, [formik.values.confirmPassword, formik.touched.confirmPassword]);

  const isFormValid = () => {
    const isPhoneValid = () => {
      if (isIndiaCountryCode(countryCode)) {
        return extractIndianNationalFromPhoneInput(formik.values.phone) !== "";
      }
      if (!formik.values.phone || formik.values.phone.trim() === "") {
        return true;
      }
      const digitsOnly = formik.values.phone.replace(/\D/g, "");
      return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    };

    const hasNameError = !formik.values.name.trim() || formik.errors.name;
    const hasBusinessNameError =
      !formik.values.businessName.trim() || formik.errors.businessName;
    const hasPasswordError = !formik.values.password || formik.errors.password;
    const hasConfirmPasswordError =
      !formik.values.confirmPassword || formik.errors.confirmPassword;
    const hasCategoryError =
      !formik.values.category.category_pid || formik.errors.category;
    const hasPhoneError = isIndiaCountryCode(countryCode)
      ? !isPhoneValid() || formik.errors.phone
      : formik.errors.phone;

    return (
      !hasNameError &&
      !hasBusinessNameError &&
      !hasPasswordError &&
      !hasConfirmPasswordError &&
      !hasCategoryError &&
      !hasPhoneError
    );
  };

  // For IN: UI collects a 10-digit national number (react-phone-input-2 may include 91).
  // API expects E.164 (+91XXXXXXXXXX) for mobile and POC phones.
  const extractPhoneNumber = (
    phoneValue: string,
    selectedCountry: string
  ): string => {
    if (isIndiaCountryCode(selectedCountry)) {
      return toE164IndiaFromPhoneInput(phoneValue);
    }
    return toE164International(phoneValue);
  };

  const handlePersonalDetails = async (values: any) => {
    const { category } = values;

    if (
      !category ||
      typeof category !== "object" ||
      !String(category.category || "").trim() ||
      !String(category.category_pid || "").trim()
    ) {
      formik.setFieldTouched("category", true, false);
      formik.setFieldError("category", "Please select one category");
      ToastWarning(
        "Invalid Category",
        "Please select a valid business category."
      );
      return;
    }

    const phoneNumber = extractPhoneNumber(values.phone, countryCode);
    if (isIndiaCountryCode(countryCode) && !phoneNumber) {
      formik.setFieldTouched("phone", true, false);
      formik.setFieldError("phone", "Phone number is required");
      ToastWarning("Phone number is required", "Please enter a valid Indian mobile number.");
      return;
    }

    const body = {
      name: values.name,
      mobile: phoneNumber,
      password: values.password,
      email: createdUserData.email,
      onboarding: {
        org_name: values.businessName,
        org_trade_name: values.businessName,
        category: String(category.category).trim(),
        category_pid: String(category.category_pid).trim(),
        country_code: countryCode?.toUpperCase(),
        business_poc: {
          name: values.name,
          email: createdUserData.email,
          designation: "Head of Business",
          phone: phoneNumber,
        },
        technical_poc: {
          name: values.name,
          email: createdUserData.email,
          designation: "CTO",
          phone: phoneNumber,
        },
      },
    };

    try {
      await callApi("/organisations/onboarding", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${createdUserData.token}`,
        },
        data: body,
      });

      localStorage.removeItem("created_user_details");

      ToastSuccess("", [
        TEXTS.SIGNUP_ONBOARDING_SUCCESS,
        "Your organisation is ready. Please sign in to continue.",
      ]);
      navigate("/login");
    } catch (err: any) {
      const message = String(err?.message || "");
      if (message.toLowerCase().includes("organisation name already exists")) {
        formik.setFieldTouched("businessName", true, false);
        formik.setFieldError(
          "businessName",
          "This business name is already in use. Choose a different name."
        );
      }
      if (message.toLowerCase().includes("log in to finish")) {
        ToastWarning("", message);
        navigate("/login");
        return;
      }
      ToastWarning("", message);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await callApi("/organisations/categories?limit=200", {
        method: "GET",
      });

      const subCategories = getSubCategoryOptions(res);
      setSubCategories(subCategories);
    } catch (err) {
      console.error("Failed to fetch organisation categories", err);
      setSubCategories([]);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await callApi("/countries", {
        method: "GET",
      });

      let countriesDetails = res.map((countryDetail: any) => ({
        name: countryDetail.name,
        code: countryDetail.code,
        id: countryDetail._id,
        dial_code: countryDetail.dial_code,
      }));

      const countryNameAsIndia = countriesDetails.find(
        (country: any) => country.name === "India"
      );

      countriesDetails = countriesDetails.filter(
        (country: any) => country.id !== countryNameAsIndia.id
      );

      countriesDetails.unshift(countryNameAsIndia);
    } catch (err: any) {
      console.log("Failed in fetching the countries");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCountries();
  }, []);

  return (
    <form
      onSubmit={(e) => {
        handleSubmitAttempt();
        formik.handleSubmit(e);
      }}
      className="d-flex flex-column shadow bg-white border rounded-4 px-4 py-4"
      style={{
        width: 500,
        boxShadow:
          "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        transition: "width 0.3s ease",
      }}
    >
      <h2 className="text-2xl font-semibold">Personal Details</h2>
      <div className="row g-3 mb-3">
        {/* Name Input */}
        <div className="col-md-6 position-relative">
          <Input
            id="signup-name"
            name="name"
            placeholder="Name"
            value={formik.values.name}
            onChange={handleNameChange}
            onBlur={formik.handleBlur}
            autoComplete="off"
          />
          {formik.touched.name && formik.errors.name && (
            <div className="text-xs text-danger d-block">
              {formik.errors.name}
            </div>
          )}
        </div>

        <div className="col-md-6 position-relative">
          <PhoneInput
            country={"in"}
            inputProps={{
              id: "signup-phone",
              "data-testid": "signup-phone",
              name: "phone",
              required: isIndiaCountryCode(countryCode),
              placeholder: isIndiaCountryCode(countryCode)
                ? "Phone Number *"
                : "Phone Number",
              onBlur: handlePhoneBlur,
              autoComplete: "off",
              autoCorrect: "off",
              autoCapitalize: "off",
              spellCheck: "false",
              onPaste: (e: React.ClipboardEvent) => e.preventDefault(), // Prevent paste
              onDrop: (e: React.DragEvent) => e.preventDefault(), // Prevent drop
            }}
            value={formik.values.phone}
            onChange={(value, data) => {
              const newCountryCode = String(
                (data as any)?.countryCode || "in"
              ).toUpperCase();

              setCountryCode(newCountryCode);
              formik.setFieldValue("countryCode", newCountryCode, false);

              // Update phone value
              formik.setFieldValue("phone", value);

              // Mark phone as touched to trigger validation
              formik.setFieldTouched("phone", true, false);

              // Clear any existing phone errors
              if (formik.errors.phone) {
                formik.setFieldError("phone", undefined);
              }

              // Validate phone after a short delay to ensure state is updated
              setTimeout(() => {
                formik.validateField("phone");
              }, 100);
            }}
            containerStyle={{ width: "100%" }}
            inputStyle={{
              width: "100%",
              height: "38px",
              fontSize: "14px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e2e5e9",
            }}
            buttonStyle={{
              borderTopLeftRadius: "4px",
              borderBottomLeftRadius: "4px",
              borderRight: "1px solid #e2e5e9",
            }}
            specialLabel={""}
            enableAreaCodes={false}
            disableDropdown={false}
            countryCodeEditable={false}
          />
          {formik.touched.phone && formik.errors.phone && (
            <div className="text-xs text-danger d-block">
              {formik.errors.phone}
            </div>
          )}
        </div>

        {/* Business Name */}
        <div className="col-md-6 position-relative">
          <Input
            id="signup-business-name"
            name="businessName"
            placeholder="Business Name"
            value={formik.values.businessName}
            onChange={handleBusinessNameChange}
            onBlur={formik.handleBlur}
            autoComplete="off"
          />
          {formik.touched.businessName && formik.errors.businessName && (
            <div className="text-xs text-danger d-block">
              {formik.errors.businessName}
            </div>
          )}
        </div>

        <div className="col-md-6 position-relative">
          <CategoryDropdown
            id="category-dropdown"
            name="category"
            formik={formik}
            subCategories={subCategories}
          />

          {formik.touched.category && formik.errors.category && (
            <div className="text-xs text-danger mt-1">
              {typeof formik.errors.category === "string"
                ? formik.errors.category
                : formik.errors.category?.category ||
                  formik.errors.category?.category_pid ||
                  "Please select one category"}
            </div>
          )}
        </div>
      </div>

      {/* Password Section */}
      <p className="mt-0 text-muted" style={{ fontSize: "16px" }}>
        Set Password
      </p>
      <section className="row g-3 mb-3">
        <div className="col-md-6 position-relative">
          <Input
            id="signup-password"
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Password"
            value={formik.values.password}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            autoComplete="off"
            suffixIcon={
              <div
                {...testIdProps("signup-password-toggle")}
                className="position-absolute"
                style={{
                  right: "2px",
                  cursor: "pointer",
                  height: "38px",
                  width: "38px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <Eye size={18} color="#C4C4C4" />
                ) : (
                  <EyeOff size={18} color="#C4C4C4" />
                )}
              </div>
            }
          />
          {formik.touched.password && formik.errors.password && (
            <div className="text-xs text-danger d-block">
              {formik.errors.password}
            </div>
          )}
        </div>

        <div className="col-md-6 position-relative">
          <Input
            id="signup-confirm-password"
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            placeholder="Confirm Password"
            value={formik.values.confirmPassword}
            onBlur={handleConfirmPasswordBlur}
            onChange={handleConfirmPasswordChange}
            autoComplete="off"
            suffixIcon={
              <div
                {...testIdProps("signup-confirm-password-toggle")}
                className="position-absolute"
                style={{ right: "10px", cursor: "pointer" }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <Eye size={18} color="#C4C4C4" />
                ) : (
                  <EyeOff size={18} color="#C4C4C4" />
                )}
              </div>
            }
          />
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <div className="capitalize text-xs text-danger d-block">
              {formik.errors.confirmPassword}
            </div>
          )}
        </div>
      </section>

      {/* Password Conditions */}
      <section className="mb-4">
        <span
          className="fw-semibold text-muted d-block mb-2"
          style={{ fontSize: "14px" }}
        >
          Password Conditions:
        </span>
        <ul className="list-unstyled small mb-0" style={{ fontSize: "12px" }}>
          {passwordCriteria.map(({ id, text, test }) => {
            const passed = test(formik.values.password);
            const visible = formik.values.password.length > 0;

            return (
              <li
                key={id}
                className={`d-flex align-items-center gap-2 ${visible ? (passed ? "text-success" : "text-danger") : "text-muted"}`}
              >
                {visible ? (
                  passed ? (
                    <Check className="me-1" size={16} />
                  ) : (
                    <X className="me-1" size={16} />
                  )
                ) : (
                  <span
                    className="d-inline-block rounded-circle bg-secondary"
                    style={{ width: 6, height: 6 }}
                  ></span>
                )}
                {text}
              </li>
            );
          })}
        </ul>
      </section>

      <StyledButton
        id="signup-submit"
        type="submit"
        isLoading={formik.isSubmitting}
        style={
          !isFormValid() || formik.isSubmitting
            ? buttonStyles.disabled.normal
            : buttonStyles.primary.normal
        }
        hoverStyle={
          !isFormValid() || formik.isSubmitting
            ? buttonStyles.disabled.hover
            : buttonStyles.primary.hover
        }
        disabled={!isFormValid() || formik.isSubmitting}
      >
        Submit
      </StyledButton>
    </form>
  );
};

export default Step3;
